import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { QrCode, Download, Trash2, Loader2, Copy, Check, BarChart3, Users, MousePointerClick, Plus } from "lucide-react";

/**
 * Phase 23 — Recipe Gift QR admin page.
 * Lets a business_admin generate / revoke table-tent QR codes and inspect the
 * leads captured by each one.
 */
export default function RecipeGiftAdminPage() {
  const [qrs, setQrs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", table_no: "", pitch: "" });
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [q, l, s] = await Promise.all([
        api.get("/recipe-gift/admin/qrs"),
        api.get("/recipe-gift/admin/leads"),
        api.get("/recipe-gift/admin/stats"),
      ]);
      setQrs(q.data.items || []);
      setLeads(l.data.items || []);
      setStats(s.data || null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const createQr = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      await api.post("/recipe-gift/admin/qrs", {
        label: form.label || `Table ${form.table_no || "Front"}`,
        table_no: form.table_no || null,
        pitch: form.pitch || null,
      });
      setForm({ label: "", table_no: "", pitch: "" });
      await refresh();
    } catch (e2) {
      setError(formatApiError(e2?.response?.data?.detail) || "Couldn't create QR.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this QR? It will stop accepting new claims.")) return;
    try {
      await api.post(`/recipe-gift/admin/qrs/${token}/revoke`);
      await refresh();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Couldn't revoke.");
    }
  };

  const copyUrl = async (url, token) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="recipe-gift-admin">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
            <QrCode className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Recipe Gift QR</h1>
            <p className="text-sm text-stone-500">Print a table-tent. Diners scan, drop their email, and walk away with a branded recipe PDF — a quiet, classy lead-gen loop.</p>
          </div>
        </div>
      </header>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="recipe-gift-stats">
          <StatCard icon={<QrCode className="w-4 h-4" />} label="QR codes" value={stats.qrs} />
          <StatCard icon={<MousePointerClick className="w-4 h-4" />} label="Scans" value={stats.scans} />
          <StatCard icon={<Users className="w-4 h-4" />} label="Leads" value={stats.leads} accent />
          <StatCard icon={<Download className="w-4 h-4" />} label="Downloads" value={stats.downloads} />
          <StatCard icon={<BarChart3 className="w-4 h-4" />} label="Conversion" value={`${stats.conversion_pct}%`} />
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="recipe-gift-admin-error">
          {error}
        </div>
      )}

      {/* Create form */}
      <section className="bg-white border border-stone-200 rounded-2xl p-5 mb-8" data-testid="recipe-gift-create">
        <h2 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New QR table-tent
        </h2>
        <form onSubmit={createQr} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input
            data-testid="recipe-gift-form-label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Label (e.g. 'Front Counter')"
            className="sm:col-span-2 rounded-lg border border-stone-200 px-3 py-2 text-sm"
          />
          <input
            data-testid="recipe-gift-form-table"
            value={form.table_no}
            onChange={(e) => setForm({ ...form, table_no: e.target.value })}
            placeholder="Table no. (opt.)"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={creating}
            data-testid="recipe-gift-form-submit"
            className="rounded-lg bg-stone-900 text-white px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Generate QR
          </button>
          <input
            data-testid="recipe-gift-form-pitch"
            value={form.pitch}
            onChange={(e) => setForm({ ...form, pitch: e.target.value })}
            placeholder="Chef's pitch (optional, shown on the landing page)"
            className="sm:col-span-4 rounded-lg border border-stone-200 px-3 py-2 text-sm"
          />
        </form>
      </section>

      {/* QR grid */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Live QR codes ({qrs.length})</h2>
        {loading && qrs.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-stone-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : qrs.length === 0 ? (
          <div className="text-sm text-stone-500 italic">No QR codes yet — generate one above.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {qrs.map((q) => (
              <div
                key={q.qr_token}
                data-testid={`recipe-gift-qr-${q.qr_token}`}
                className={`bg-white border rounded-2xl p-4 flex flex-col ${q.revoked ? "border-stone-200 opacity-60" : "border-stone-200"}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-sm font-semibold text-stone-900">{q.label}</div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-400">
                      {q.table_no ? `Table ${q.table_no}` : "All locations"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-stone-500">{q.lead_count} leads</div>
                    <div className="text-[11px] text-stone-400">{q.scan_count} scans</div>
                  </div>
                </div>
                {q.qr_data_url && (
                  <img
                    src={q.qr_data_url}
                    alt="QR"
                    className="w-full aspect-square object-contain bg-white border border-stone-100 rounded-lg p-3"
                    data-testid={`recipe-gift-qr-img-${q.qr_token}`}
                  />
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => copyUrl(q.landing_url, q.qr_token)}
                    data-testid={`recipe-gift-qr-copy-${q.qr_token}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                  >
                    {copied === q.qr_token ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === q.qr_token ? "Copied" : "Copy URL"}
                  </button>
                  <a
                    href={q.qr_data_url}
                    download={`recipe-gift-${q.qr_token}.png`}
                    data-testid={`recipe-gift-qr-dl-${q.qr_token}`}
                    aria-label="Download QR as PNG"
                    title="Download QR as PNG"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                  >
                    <Download className="w-3.5 h-3.5" /> PNG
                  </a>
                  {!q.revoked && (
                    <button
                      onClick={() => revoke(q.qr_token)}
                      data-testid={`recipe-gift-qr-revoke-${q.qr_token}`}
                      aria-label="Revoke this QR code"
                      title="Revoke this QR code"
                      className="inline-flex items-center justify-center px-2.5 py-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Leads table */}
      <section>
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Captured leads ({leads.length})</h2>
        {leads.length === 0 ? (
          <div className="text-sm text-stone-500 italic">Leads will appear here once diners scan and claim.</div>
        ) : (
          <div className="overflow-x-auto bg-white border border-stone-200 rounded-2xl">
            <table className="w-full text-sm" data-testid="recipe-gift-leads-table">
              <thead className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Captured</th>
                  <th className="text-left px-4 py-2.5">Downloaded</th>
                  <th className="text-left px-4 py-2.5">Opt-in</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.pdf_token} className="border-t border-stone-100">
                    <td className="px-4 py-2.5 text-stone-900">{l.email}</td>
                    <td className="px-4 py-2.5 text-stone-700">{l.name || "—"}</td>
                    <td className="px-4 py-2.5 text-stone-500">{new Date(l.captured_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-stone-500">{l.downloaded_at ? new Date(l.downloaded_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${l.opt_in ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                        {l.opt_in ? "Yes" : "No"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, accent = false }) {
  return (
    <div className={`bg-white border rounded-2xl p-4 ${accent ? "border-amber-200" : "border-stone-200"}`}>
      <div className="flex items-center gap-2 text-stone-500 text-[11px] uppercase tracking-widest">
        {icon}{label}
      </div>
      <div className="text-2xl font-semibold mt-1 text-stone-900">{value}</div>
    </div>
  );
}
